import { useCallback } from 'react'
import {
  ProcessCellForExportParams,
  ShouldRowBeSkippedParams,
  GridApi,
  GridOptions,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import { getLabel } from '../../../../lib/commons/i18nLabel'
import { TAG_DELIMITER } from '../../../../lib/functions/tag'
import { TicketBasic } from '../../../../lib/functions/ticket'
import store from '../../../../store'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { format as commentFormat } from '../../../../utils/comment'
import { exportExcel } from '../../../containers/BulkSheet/excel'
import { ColumnType } from '../../../containers/commons/AgGrid'
import {
  formatPercentage,
  getStatusSummary,
} from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus/cellRenderer'
import formatter from '../../../containers/meta/formatter'
import { createNewTicketRow } from '../dataManager'
import {
  MULTI_PROPERTY_DELIMITER,
  TicketRow,
  TicketWbsItemRow,
} from '../tickets'
import TicketListApi, {
  TicketListBasic,
  TicketListDetail,
} from '../../../../lib/functions/ticketList'
import { WbsItemCodeGenerator } from '../../../../applications/ports/wbsItemCodeService'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import {
  ExcelImportColumnInfo,
  ExcelSource,
  useWbsItemExcelParser,
} from '../../../containers/BulkSheetView/hooks/wbsItemExcelParser'
import { FlagxsColumnDef } from '../../../containers/BulkSheetView/gridOptions/extension'
import {
  EXTERNAL_ID_CODE,
  EXTERNAL_ID_TICKET_TYPE,
  EXTERNAL_ID_STATUS,
  EXTERNAL_ID_SUBSTATUS,
  EXTERNAL_ID_TICKET_LIST,
  EXTERNAL_ID_TAG,
  EXTERNAL_ID_DISPLAY_NAME,
  EXTERNAL_ID_PRIORITY,
  EXTERNAL_ID_DIFFICULTY,
  EXTERNAL_ID_DESCRIPTION,
  EXTERNAL_ID_TEAM,
  EXTERNAL_ID_ACCOUNTABLE,
  EXTERNAL_ID_RESPONSIBLE,
  EXTERNAL_ID_ASSIGNEE,
  EXTERNAL_ID_WATCHERS,
  EXTERNAL_ID_ESTIMATED_STORYPOINT,
  EXTERNAL_ID_ESTIMATED_WORKLOAD,
  EXTERNAL_ID_SPRINT,
  EXTERNAL_ID_SCHEDULED_START_DATE,
  EXTERNAL_ID_SCHEDULED_END_DATE,
  EXTERNAL_ID_ACTUAL_START_DATE,
  EXTERNAL_ID_ACTUAL_END_DATE,
} from '../GridOptions/columnDefs/columns'
import Workload from '../../../../lib/functions/workload'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'

const DEFAULT_IMPORTABLE_COLUMN_EXTERNAL_IDS: string[] = [
  EXTERNAL_ID_CODE,
  EXTERNAL_ID_TICKET_TYPE,
  EXTERNAL_ID_STATUS,
  EXTERNAL_ID_SUBSTATUS,
  EXTERNAL_ID_TICKET_LIST,
  EXTERNAL_ID_TAG,
  EXTERNAL_ID_DISPLAY_NAME,
  EXTERNAL_ID_PRIORITY,
  EXTERNAL_ID_DIFFICULTY,
  EXTERNAL_ID_DESCRIPTION,
  EXTERNAL_ID_TEAM,
  EXTERNAL_ID_ACCOUNTABLE,
  EXTERNAL_ID_RESPONSIBLE,
  EXTERNAL_ID_ASSIGNEE,
  EXTERNAL_ID_WATCHERS,
  EXTERNAL_ID_ESTIMATED_STORYPOINT,
  EXTERNAL_ID_ESTIMATED_WORKLOAD,
  EXTERNAL_ID_SPRINT,
  EXTERNAL_ID_SCHEDULED_START_DATE,
  EXTERNAL_ID_SCHEDULED_END_DATE,
  EXTERNAL_ID_ACTUAL_START_DATE,
  EXTERNAL_ID_ACTUAL_END_DATE,
]

export const useExcel = (
  projectUuid: string,
  gridOptions: GridOptions | undefined,
  gridApi: GridApi | undefined,
  data: TicketRow[],
  setLoading: (val: boolean) => void,
  setData: (data: TicketRow[]) => void,
  generateCode: WbsItemCodeGenerator,
  wbsItemAdditionalProperties: WbsItemAdditionalPropertyLayoutEntity | undefined
) => {
  // Export -------------
  const customExportValues = useCallback(
    (params: ProcessCellForExportParams<TicketRow>) => {
      if (!params.node) return undefined
      const columnId: string = params.column.getColId()
      const colDef = params.column.getColDef()
      const { cellRendererParams, cellEditorParams, type } = colDef
      const row: TicketRow | undefined = params.node.data

      if (columnId === 'rowNumber') {
        const rendererParams = cellRendererParams({ node: params.node })
        return rendererParams.value
      }
      if (columnId === 'wbsItem.ticketType') {
        const wbsItemType = row?.wbsItem?.wbsItemType
        return wbsItemType?.isTicket() ? wbsItemType?.name : ''
      }
      if (columnId === 'wbsItem.status') {
        if (row?.isTotal) {
          const { countTotal, countDone } = getStatusSummary(
            params.api,
            columnId
          )
          const percentage = formatPercentage(countDone, countTotal)
          const done = formatter.format(
            countDone,
            colDef.cellRendererParams.uiMeta
          )
          const total = formatter.format(
            countTotal,
            colDef.cellRendererParams.uiMeta
          )
          return `${percentage} (${done} / ${total})`
        }
        return undefined
      }
      if (columnId === 'wbsItem.sprints') {
        return params.value?.map(v => v.name)?.join(', ') ?? ''
      }
      if (columnId === 'wbsItem.tags') {
        return params.value?.map(v => v.name)?.join(TAG_DELIMITER)
      }
      if (columnId === 'commentSummary.latestComment') {
        return params.value ? commentFormat(params.value) : ''
      }
      if (columnId === 'wbsItem.watchers') {
        return (
          params.value?.map(v => v.name).join(MULTI_PROPERTY_DELIMITER) ?? ''
        )
      }
      if (!type) {
        if (cellEditorParams) {
          const { entity } = cellEditorParams
          if (entity) {
            // For extension EntitySearch column
            const options = params.context[entity] ?? []
            const option = options.find(v =>
              [params.value, params.value?.uuid].includes(v.uuid)
            )
            return (
              getLabel(option?.nameI18n) ||
              option?.displayName ||
              option?.name ||
              ''
            )
          }
        }
        if (typeof params.value === 'object') {
          return params.value.name ?? params.value.displayName ?? undefined
        }
      } else {
        if (type.includes(ColumnType.multiSelect)) {
          return (
            params?.value?.map(v => v.name).join(MULTI_PROPERTY_DELIMITER) ?? ''
          )
        }
      }
      return undefined
    },
    []
  )

  const onExcelExport = useCallback(
    (colIds: string[]) => {
      if (!gridApi) return

      exportExcel({
        fileNamePrefix: 'ticket_list_edit',
        gridApi,
        exportColIds: colIds,
        shouldRowBeSkipped: (params: ShouldRowBeSkippedParams) =>
          !params.node.displayed,
        getCustomExportValue: customExportValues,
      })
    },
    [gridApi, customExportValues]
  )

  // Import -------------
  const {
    readExcelSource,
    getExcelImportColumnInfo,
    parseWorkLoadHour,
    findFromContext,
    mergeCommonWbsItemUpdatedRow,
    createErrorMessage,
    hasWbsItemRowDiff,
  } = useWbsItemExcelParser(projectUuid)

  const mergeWbsItemColumnUpdatedRow = useCallback(
    async (
      embodied: TicketWbsItemRow,
      source: ExcelSource,
      excelColInfo: ExcelImportColumnInfo,
      context: any,
      dailyWorkHours: number,
      monthlyWorkDays: number
    ): Promise<TicketWbsItemRow> => {
      const keyTicketList = excelColInfo.keyMap[EXTERNAL_ID_TICKET_LIST]
      const excelKeyEstimatedHour =
        excelColInfo.keyMap[EXTERNAL_ID_ESTIMATED_WORKLOAD]

      const estimatedHour = excelKeyEstimatedHour
        ? parseWorkLoadHour(
            source[excelKeyEstimatedHour],
            context['workloadUnitState']?.hoursPerSelectedUnit || 1
          )
        : embodied.estimatedHour

      const margedRow = await mergeCommonWbsItemUpdatedRow(
        embodied,
        source,
        {
          code: EXTERNAL_ID_CODE,
          displayName: EXTERNAL_ID_DISPLAY_NAME,
          status: EXTERNAL_ID_STATUS,
          substatus: EXTERNAL_ID_SUBSTATUS,
          priority: EXTERNAL_ID_PRIORITY,
          difficulty: EXTERNAL_ID_DIFFICULTY,
          description: EXTERNAL_ID_DESCRIPTION,
          team: EXTERNAL_ID_TEAM,
          accountable: EXTERNAL_ID_ACCOUNTABLE,
          responsible: EXTERNAL_ID_RESPONSIBLE,
          assignee: EXTERNAL_ID_ASSIGNEE,
          watchers: EXTERNAL_ID_WATCHERS,
          estimatedStorypoint: EXTERNAL_ID_ESTIMATED_STORYPOINT,
          scheduledStartDate: EXTERNAL_ID_SCHEDULED_START_DATE,
          scheduledEndDate: EXTERNAL_ID_SCHEDULED_END_DATE,
          actualStartDate: EXTERNAL_ID_ACTUAL_START_DATE,
          actualEndDate: EXTERNAL_ID_ACTUAL_END_DATE,
          tag: EXTERNAL_ID_TAG,
          sprint: EXTERNAL_ID_SPRINT,
        },
        excelColInfo,
        context
      )

      return {
        ...embodied,
        ...margedRow,
        ticketListUuid: keyTicketList
          ? findFromContext(context, 'ticketList', source[keyTicketList])?.uuid
          : embodied.ticketListUuid,
        estimatedHour,
        estimatedWorkload: Workload.from({
          hour: estimatedHour,
          standard: {
            dailyWorkHours,
            monthlyWorkDays,
          },
        }),
      } as TicketWbsItemRow
    },
    [findFromContext, parseWorkLoadHour, mergeCommonWbsItemUpdatedRow]
  )

  const hasDiff = useCallback(
    (ab: TicketRow, bb: TicketRow): boolean => {
      const a = ab.wbsItem!
      const b = bb.wbsItem!
      return (
        hasWbsItemRowDiff(a, b) ||
        ab.ticketList?.code !== bb.ticketList?.code ||
        ab.ticketList?.displayName !== bb.ticketList?.displayName ||
        ab.ticketList?.projectUuid !== bb.ticketList?.projectUuid ||
        ab.ticketList?.ticketType !== bb.ticketList?.ticketType ||
        ab.ticketList?.uuid !== bb.ticketList?.uuid ||
        ab.ticketList?.wbsItemUuid !== bb.ticketList?.wbsItemUuid
      )
    },
    [hasWbsItemRowDiff]
  )

  const mergeUpdatedRow = useCallback(
    async (
      embodiedRow: TicketRow,
      excelRow: ExcelSource,
      excelColInfo: ExcelImportColumnInfo,
      context: any,
      dailyWorkHours: number,
      monthlyWorkDays: number
    ) => {
      return {
        ...embodiedRow,
        wbsItem: embodiedRow.wbsItem
          ? await mergeWbsItemColumnUpdatedRow(
              embodiedRow.wbsItem,
              excelRow,
              excelColInfo,
              context,
              dailyWorkHours,
              monthlyWorkDays
            )
          : undefined,
      }
    },
    [mergeWbsItemColumnUpdatedRow]
  )

  const parseImportData = useCallback(
    async ({
      projectUuid,
      source,
      columnDefs,
      wbsItemAdditionalProperties,
      rowData,
      ticketType,
      ticketTypes,
      defaultTicketList,
    }: {
      projectUuid: string
      source: ExcelSource[]
      columnDefs: FlagxsColumnDef[]
      wbsItemAdditionalProperties:
        | WbsItemAdditionalPropertyLayoutEntity
        | undefined
      rowData: TicketRow[]
      ticketType: string
      ticketTypes: WbsItemTypeVO[]
      defaultTicketList: TicketListBasic | undefined
    }) => {
      // Initialize -----

      // create column key map
      const excelInfo: ExcelImportColumnInfo = await getExcelImportColumnInfo(
        DEFAULT_IMPORTABLE_COLUMN_EXTERNAL_IDS,
        columnDefs,
        source,
        wbsItemAdditionalProperties
      )
      const colKeyMap = excelInfo.keyMap

      // Filter Header and Total rows
      // If the displayName and ticketList are null, it will be considered a total row.
      const excelRows = source
        .slice(1)
        .filter(
          src =>
            src &&
            (src[colKeyMap[EXTERNAL_ID_CODE]] ||
              src[colKeyMap[EXTERNAL_ID_DISPLAY_NAME]] ||
              src[colKeyMap[EXTERNAL_ID_TICKET_LIST]])
        )

      const organization = store.getState().tenant.organization
      const dailyWorkHours = organization!.dailyWorkHours
      const monthlyWorkDays = organization!.monthlyWorkDays

      const ticketLists: TicketListBasic[] = (
        await TicketListApi.getAll({})
      ).json.map(
        (detail: TicketListDetail) =>
          ({
            uuid: detail.uuid,
            code: detail.wbsItem?.code,
            wbsItemUuid: detail.wbsItem?.uuid,
            projectUuid: detail.wbsItem?.projectUuid,
            displayName: detail.wbsItem?.displayName,
            ticketType: detail.ticketType,
            ticketListUuid: detail.uuid,
          }) as TicketBasic
      )

      // parse
      const errorMessages: string[] = []
      const updatedData = new Map<string, TicketRow>()
      const addedData: TicketRow[] = []
      const embodiedRows: TicketRow[] = []
      rowData.forEach((row: TicketRow) => {
        if (!row.wbsItem) return
        embodiedRows.push(row)
      })

      for (let index = 0; index < excelRows.length; index++) {
        const excelRow = excelRows[index]
        const ticketList: TicketListBasic | undefined =
          ticketLists.find(
            (ticketList: TicketListBasic) =>
              ticketList.displayName ===
              excelRow[colKeyMap[EXTERNAL_ID_TICKET_LIST]]?.toString()
          ) ?? defaultTicketList
        // validaton
        if (!ticketList) {
          errorMessages.push(
            createErrorMessage(index + 1, 'tickets.import.ticketType.required')
          )
          continue
        } else if (ticketList?.ticketType !== ticketType.toString()) {
          const ticketTypeVO = ticketTypes.find(t => t.code === ticketType)
          errorMessages.push(
            createErrorMessage(index + 1, 'tickets.import.invalid.ticketType', {
              ticketType: ticketTypeVO?.name ?? ticketType,
            })
          )
          continue
        }

        const isAdded = !excelRow[colKeyMap[EXTERNAL_ID_CODE]]
        // Prepare import
        if (!isAdded) {
          // Edited data
          const embodiedRow: TicketRow | undefined = embodiedRows.find(
            v =>
              v.wbsItem!.code ===
              excelRow[colKeyMap[EXTERNAL_ID_CODE]]?.toString()
          )
          if (!embodiedRow?.wbsItem) {
            errorMessages.push(
              createErrorMessage(
                index + 1,
                'tickets.import.not.found.embodied.row',
                {
                  code: excelRow[colKeyMap[EXTERNAL_ID_CODE]],
                }
              )
            )
            continue
          }
          if (errorMessages.length) continue
          const mergedRow = await mergeUpdatedRow(
            embodiedRow,
            excelRow,
            excelInfo,
            gridOptions?.context,
            dailyWorkHours,
            monthlyWorkDays
          )

          const newRow = {
            ...mergedRow,
            edited: true,
            ticketList: ticketList,
          } as TicketRow
          if (hasDiff(embodiedRow, newRow)) {
            updatedData.set(embodiedRow.uuid, newRow)
          }
        } else {
          const createRandomCode = generateCode()
          excelRow[colKeyMap[EXTERNAL_ID_CODE]] = createRandomCode

          if (errorMessages.length) continue
          // Added data
          const newRow = createNewTicketRow(ticketList, generateCode)
          const mergedRow = await mergeUpdatedRow(
            newRow,
            excelRow,
            excelInfo,
            gridOptions?.context,
            dailyWorkHours,
            monthlyWorkDays
          )

          const newTicketRow = {
            ...mergedRow,
            ticketList: ticketList,
          } as TicketRow
          addedData.push(newTicketRow)
        }
      }
      const newData: TicketRow[] = []
      embodiedRows.forEach(row => {
        const updated = updatedData.get(row.uuid)
        if (updated) {
          newData.push(updated)
        } else {
          newData.push(row)
        }
      })
      return {
        rows: newData.concat(addedData),
        errorMessages,
      }
    },
    [
      gridOptions?.context,
      generateCode,
      getExcelImportColumnInfo,
      createErrorMessage,
      hasDiff,
      mergeUpdatedRow,
    ]
  )

  const onExcelImport = useCallback(
    async (
      ticketType: string,
      ticketTypes: WbsItemTypeVO[],
      defaultTicketList: TicketListBasic | undefined,
      raw: Uint8Array
    ) => {
      if (!gridOptions?.context || !gridOptions?.columnDefs) return
      try {
        setLoading(true)
        // Convert excel file
        const source: ExcelSource[] = readExcelSource(raw)
        const { rows: newData, errorMessages } = await parseImportData({
          projectUuid,
          source,
          columnDefs: gridOptions.columnDefs as FlagxsColumnDef[],
          wbsItemAdditionalProperties,
          rowData: data,
          ticketType,
          ticketTypes,
          defaultTicketList,
        })
        if (errorMessages.length > 0) {
          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({ id: 'projectPlan.import.warning' }),
              text: errorMessages.join('\n'),
            })
          )
          return
        }
        setData(newData)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, gridOptions, setLoading, setData]
  )

  return {
    onExcelExport,
    onExcelImport,
  }
}
